import React from 'react';
import api from 'lib/trpcClient';
import { useLayoutContext } from 'contexts/LayoutContext';
import {
  createEqualisationEditorRoute,
  EQUALISATIONS_ADD,
} from 'adminConstants/routes';
import { Edit, Delete, SyncAlt } from '@mui/icons-material';
import { ActionStatusLabels } from 'further-ui/labels';
import { ActionStatus } from 'further-types/lp/action';
import Page from 'components/new/Page';
import Table, { Column as TableColumn } from 'further-ui/components/Table';
import ConfirmDialog from 'further-ui/components/ConfirmDialog';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';
import { useDisclosure } from 'further-ui/hooks';
import { getQueryKey } from '@trpc/react-query';

const EqualisationsContent: React.FC = () => {
  const { vehicle } = useLayoutContext();
  const [canEdit, canDelete] = useHasPermissions([
    RolePermission.LpEditEqualisation,
    RolePermission.LpDeleteEqualisation,
  ]);
  const [equalisationsData] = api.equalisations.list.useSuspenseQuery({
    vehicleId: vehicle?._id ?? '',
  });

  const listEqualisationsQueryKey = getQueryKey(api.equalisations.list, {
    vehicleId: vehicle?._id ?? '',
  });

  const deleteDisclosure = useDisclosure<{ id: string }>();

  const mutationOpts = useHandleTrpcMutation<{
    success: boolean;
  }>();

  const deleteEqualisation = api.equalisations.delete.useMutation(
    mutationOpts({
      successMessage: 'Close deleted successfully.',
      invalidations: [listEqualisationsQueryKey],
      onSuccess: () => {
        deleteDisclosure.onClose();
      },
    }),
  );

  const handleDeleteEqualisation = () => {
    deleteEqualisation.mutate({
      equalisationId: deleteDisclosure.actionProps.id,
      vehicleId: vehicle?._id ?? '',
    });
  };

  const equalisations = equalisationsData?.results ?? [];

  const columns: Array<TableColumn<(typeof equalisations)[number]>> = [
    {
      key: 'name',
      label: 'Close name',
      width: 250,
      render: (row) => row.name,
    },
    {
      key: 'closeDate',
      label: 'Close date',
      width: 140,
      render: (row) => row.closeDate.toReadable(),
    },
    {
      key: 'status',
      label: 'Status',
      width: 100,
      render: (row) =>
        row.status === ActionStatus.Approved
          ? 'Submitted'
          : ActionStatusLabels[row.status],
    },
  ];

  return (
    <>
      <Table
        actions={[
          {
            label: 'Edit',
            icon: Edit,
            link: (row) => createEqualisationEditorRoute(row.id ?? ''),
            disabled: (row) => !row.isEditable || !canEdit,
          },
          {
            label: 'Delete',
            icon: Delete,
            onClick: (row) =>
              deleteDisclosure.stageAction({ id: row.id ?? '' }),
            disabled: (row) =>
              !row.canBeDeleted || deleteEqualisation.isPending || !canDelete,
          },
        ]}
        onRowClickHref={(row) =>
          row.isEditable && canEdit
            ? createEqualisationEditorRoute(row.id ?? '')
            : undefined
        }
        columns={columns}
        scrollable
        rows={equalisations}
      />
      <ConfirmDialog
        maxWidth="sm"
        onClose={deleteDisclosure.onClose}
        open={deleteDisclosure.isOpen}
        loading={deleteEqualisation.isPending}
        title="Deleting close"
        content="You are about to delete this fund close. This can't be undone."
        actions={{
          confirm: {
            label: 'Delete close',
            onClick: handleDeleteEqualisation,
          },
          cancel: {
            label: 'Cancel',
            onClick: deleteDisclosure.onClose,
          },
        }}
      />
    </>
  );
};

const Equalisations: React.FC = () => {
  return (
    <Page
      tabTitle="Close & Equalise: View Closes"
      actions={[
        {
          label: 'Add new',
          variant: 'contained',
          link: EQUALISATIONS_ADD,
        },
      ]}
      headingIcon={SyncAlt}
      spacing="md"
      heading="Closes"
      withHeadingDivider
    >
      <EqualisationsContent />
    </Page>
  );
};

export default Equalisations;
